import type { AsyncStatus } from "@react-hookz/web";
import { useCallback, useEffect, useState } from "react";
import { useStore } from "zustand";

import { unifiedFilterStore } from "../../store/unifiedFilters";

import { useGetAiRecommendedInsightsAsync } from "./useAiRecommendedInsightsAction";

export type { CardWithRecommendation } from "./useAiRecommendedInsightsAction";

export function useAiRecommendedInsightsLoader(): [
    AsyncStatus,
    any[],
    Map<string, string>,
] {
    const [contents, setContents] = useState<any[]>([]);
    const [recommendations, setRecommendations] = useState<Map<string, string>>(
        new Map(),
    );
    const [loadingStatus, setLoadingStatus] =
        useState<AsyncStatus>("not-executed");

    const handleCardReceived = useCallback((card: any) => {
        setContents((prev) => {
            const newContents = [...prev, card];
            if (newContents.length === 1) {
                setLoadingStatus("success");
            }
            return newContents;
        });
    }, []);

    const handleRecommendationChunk = useCallback(
        (identifier: string, chunk: string) => {
            setRecommendations((prev) => {
                const updated = new Map(prev);
                const current = updated.get(identifier) || "";
                updated.set(identifier, current + chunk);
                return updated;
            });
        },
        [],
    );

    const [aiState, aiAction] = useGetAiRecommendedInsightsAsync(
        handleCardReceived,
        handleRecommendationChunk,
    );

    const isAiSearchTriggered = useStore(
        unifiedFilterStore,
        (state) => state.isAiSearchTriggered,
    );

    const setIsAiSearchTriggered = useStore(
        unifiedFilterStore,
        (state) => state.setIsAiSearchTriggered,
    );

    useEffect(() => {
        if (isAiSearchTriggered) {
            setContents([]);
            setRecommendations(new Map());
            setLoadingStatus("loading");
            void aiAction
                .execute()
                .then(() => {
                    setIsAiSearchTriggered(false);
                })
                .catch((error) => {
                    console.error("AI Search - Error:", error);
                    setLoadingStatus("error");
                    setIsAiSearchTriggered(false);
                });
        }
    }, [isAiSearchTriggered, aiAction, setIsAiSearchTriggered]);

    return [loadingStatus, contents, recommendations];
}
